import React from 'react';
import { Calendar } from 'lucide-react';

interface CustomDates {
  startDate: string;
  endDate: string;
  singleDate: string;
}

interface DateSelectorProps {
  mode: 'single' | 'range';
  dates: CustomDates;
  onModeChange: (mode: 'single' | 'range') => void;
  onDateChange: (dates: CustomDates) => void;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  mode,
  dates,
  onModeChange,
  onDateChange
}) => {
  // Get today's date in YYYY-MM-DD format (max date allowed)
  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const maxDate = getTodayDate();

  // Handle date change with validation
  const handleSingleDateChange = (value: string) => {
    if (value <= maxDate) {
      onDateChange({ ...dates, singleDate: value });
    }
  };

  const handleStartDateChange = (value: string) => {
    if (value <= maxDate) {
      const newDates = { ...dates, startDate: value };
      // Ensure end date is not before start date
      if (dates.endDate && value > dates.endDate) {
        newDates.endDate = value;
      }
      onDateChange(newDates);
    }
  };

  const handleEndDateChange = (value: string) => {
    if (value <= maxDate) {
      // Ensure end date is not before start date
      if (!dates.startDate || value >= dates.startDate) {
        onDateChange({ ...dates, endDate: value });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
        <div className="p-2 bg-[#E3F0F9] rounded-lg">
          <Calendar size={18} className="text-[#0084CF]" />
        </div>
        <h3 className="text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>Date Selection</h3>
      </div>

      {/* Mode Selector */}
      <div className="flex flex-wrap items-center gap-6 pb-4 border-b border-gray-100">
        <label className="inline-flex items-center gap-3 text-sm cursor-pointer group">
          <input
            type="radio"
            checked={mode === 'single'}
            onChange={() => onModeChange('single')}
            className="w-5 h-5 text-[#0084CF] focus:ring-2 focus:ring-[#0084CF] cursor-pointer"
          />
          <span className="font-extrabold text-gray-950 group-hover:text-[#0084CF] transition-colors" style={{ color: '#16202B', fontWeight: 800 }}>
            Single Date
          </span>
        </label>
        <label className="inline-flex items-center gap-3 text-sm cursor-pointer group">
          <input
            type="radio"
            checked={mode === 'range'}
            onChange={() => onModeChange('range')}
            className="w-5 h-5 text-[#0084CF] focus:ring-2 focus:ring-[#0084CF] cursor-pointer"
          />
          <span className="font-extrabold text-gray-950 group-hover:text-[#0084CF] transition-colors" style={{ color: '#16202B', fontWeight: 800 }}>
            Date Range
          </span>
        </label>
      </div>

      {/* Date Inputs */}
      {mode === 'single' ? (
        <div className="space-y-3">
          <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
            Select Date
          </label>
          <input
            type="date"
            value={dates.singleDate}
            max={maxDate}
            onChange={(e) => handleSingleDateChange(e.target.value)}
            placeholder="mm/dd/yyyy"
            className="w-full px-4 py-3.5 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all placeholder:text-gray-400 placeholder:font-light"
            style={{ color: '#16202B', fontWeight: 400 }}
          />
          <p className="text-xs text-gray-500 font-semibold" style={{ color: '#6B7A8B', fontWeight: 600 }}>
            You can select any date up to today.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5">
          <div className="space-y-3">
            <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
              From Date
            </label>
            <input
              type="date"
              value={dates.startDate}
              max={maxDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              placeholder="mm/dd/yyyy"
              className="w-full px-4 py-3.5 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all placeholder:text-gray-400 placeholder:font-light"
              style={{ color: '#16202B', fontWeight: 400 }}
            />
          </div>
          <div className="space-y-3">
            <label className="block text-sm font-extrabold text-gray-950" style={{ color: '#16202B', fontWeight: 800 }}>
              To Date
            </label>
            <input
              type="date"
              value={dates.endDate}
              min={dates.startDate || undefined}
              max={maxDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              placeholder="mm/dd/yyyy"
              className="w-full px-4 py-3.5 bg-white text-gray-950 text-base font-normal rounded-xl border-2 border-gray-300 focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] shadow-sm transition-all placeholder:text-gray-400 placeholder:font-light"
              style={{ color: '#16202B', fontWeight: 400 }}
            />
          </div>
          <p className="col-span-2 text-xs text-gray-500 font-semibold" style={{ color: '#6B7A8B', fontWeight: 600 }}>
            You can select any date up to today. To Date must be on or after From Date.
          </p>
        </div>
      )}
    </div>
  );
};

export default DateSelector;
